import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './Hero.css';

const Hero = ({ 
  onAboutClick, 
  onContactClick, 
  onParcoursClick, 
  onTravauxClick, 
  onCompetencesClick 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const particlesRef = useRef(null);
  const nameRef = useRef(null);
  
  // Variables pour les particules
  const numParticles = 100;
  const particles = useRef([]);
  
  // Animation du texte du titre
  useEffect(() => {
    setTimeout(() => {
      setIsLoaded(true);
    }, 300);
  }, []);
  
  // Initialisation des particules
  useEffect(() => {
    if (particlesRef.current) {
      for (let i = 0; i < numParticles; i++) {
        particles.current.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 2 - 1,
          speedY: Math.random() * 2 - 1,
          opacity: Math.random() * 0.5 + 0.1,
        });
      }
      
      const canvas = particlesRef.current;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      animateParticles();
    }
    
    // Nettoyage
    return () => {
      particles.current = [];
    };
  }, []);
  
  // Animation des particules
  const animateParticles = () => {
    if (!particlesRef.current) return;
    
    const canvas = particlesRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particles.current.forEach(particle => {
      // Mouvement
      particle.x += particle.speedX;
      particle.y += particle.speedY;
      
      // Rebond sur les bords
      if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
      if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      
      // Dessin de la particule
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(179, 135, 246, ${particle.opacity})`;
      ctx.fill();
      
      // Connexion entre particules proches
      particles.current.forEach(otherParticle => {
        const dx = particle.x - otherParticle.x;
        const dy = particle.y - otherParticle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(otherParticle.x, otherParticle.y);
          ctx.strokeStyle = `rgba(179, 135, 246, ${0.2 - distance/500})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      });
    });
    
    requestAnimationFrame(animateParticles);
  };
  
  // Effet de suivi du curseur
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setMousePosition({ x, y });
      
      // Animation du nom au survol
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  const handleKeyDown = (handler) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handler();
    }
  };
  
  // Définition de la position du gradient qui suit la souris
  const gradientPosition = `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(179, 135, 246, 0.2) 0%, rgba(92, 122, 250, 0.1) 40%, transparent 70%)`;

  return (
    <>
      <Helmet>
        <title>Portfolio de Hamza TAKARROUHT | Développeur Web</title>
        <meta name="description" content="Portfolio professionnel de Hamza TAKARROUHT, développeur web full-stack passionné par les technologies modernes." />
      </Helmet>
      
      <div className="hero-section" ref={containerRef}>
        {/* Background image avec overlay */}
        <div className="hero-background"></div>
        
        {/* Effet de particules en arrière-plan */}
        <canvas 
          ref={particlesRef} 
          className="particles-canvas"
        />
        
        {/* Gradient qui suit la souris */}
        <div 
          className="mouse-gradient"
          style={{ background: gradientPosition }}
        />
        
        {/* Boutons de navigation latéraux */}
        <div className="side-nav">
          <div 
            className={`side-nav-button about-button ${isLoaded ? 'loaded' : ''}`}
            onClick={onAboutClick}
            onKeyDown={handleKeyDown(onAboutClick)}
            role="button"
            aria-label="Voir la section À propos"
            tabIndex={0}
          >
            <span className="button-text">À propos</span>
            <span className="button-icon">
              <i className="fas fa-user"></i>
            </span>
          </div>
          
          <div 
            className={`side-nav-button contact-button ${isLoaded ? 'loaded' : ''}`}
            onClick={onContactClick}
            onKeyDown={handleKeyDown(onContactClick)}
            role="button"
            aria-label="Voir la section Contact"
            tabIndex={0}
          >
            <span className="button-text">Contact</span>
            <span className="button-icon">
              <i className="fas fa-envelope"></i>
            </span>
          </div>
        </div>
        
        {/* Contenu principal */}
        <div className="hero-content">
          <div className={`hero-title ${isLoaded ? 'loaded' : ''}`}>
            <h1>
              <span className="first-name">Hamza</span>
              <span className="last-name">TAKARROUHT</span>
            </h1>
            <div className="title-bar"></div>
          </div>
          
          <div className={`hero-subtitle ${isLoaded ? 'loaded' : ''}`}>
            <span className="subtitle-text">Développeur Web Full-Stack</span>
          </div>
          
          <div className="hero-buttons">
            <div 
              className={`hero-button parcours-button ${isLoaded ? 'loaded' : ''}`}
              onClick={onParcoursClick}
              onKeyDown={handleKeyDown(onParcoursClick)}
              role="button"
              aria-label="Voir mon parcours"
              tabIndex={0}
              style={{ '--delay': '0.4s' }}
            >
              <span className="button-text">Parcours</span>
              <span className="button-icon">
                <i className="fas fa-road"></i>
              </span>
            </div>
            
            <div 
              className={`hero-button travaux-button ${isLoaded ? 'loaded' : ''}`}
              onClick={onTravauxClick}
              onKeyDown={handleKeyDown(onTravauxClick)}
              role="button"
              aria-label="Voir mes travaux"
              tabIndex={0}
              style={{ '--delay': '0.6s' }}
            >
              <span className="button-text">Travaux</span>
              <span className="button-icon">
                <i className="fas fa-briefcase"></i>
              </span>
            </div>
            
            <div 
              className={`hero-button competences-button ${isLoaded ? 'loaded' : ''}`}
              onClick={onCompetencesClick}
              onKeyDown={handleKeyDown(onCompetencesClick)}
              role="button"
              aria-label="Voir mes compétences"
              tabIndex={0}
              style={{ '--delay': '0.8s' }}
            >
              <span className="button-text">Compétences</span>
              <span className="button-icon">
                <i className="fas fa-code"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Hero;